type DepositTerms = {
  type?: string | null;
  value?: number | string | null;
  due?: string | null;
  due_hours_before_service?: number | string | null;
};

type BillingTermsLike = {
  deposit?: DepositTerms | null;
};

type PaymentRequestTermsLike = {
  collection?: {
    type?: string | null;
    percent?: number | string | null;
  } | null;
  invoices?: Array<{
    billing_terms_snapshot_json?: BillingTermsLike | null;
  }>;
};

function validPercent(value: unknown): number | null {
  const percent = Number(value);
  return Number.isFinite(percent) && percent > 0 && percent <= 100
    ? percent
    : null;
}

export function resolveDepositPercent(
  snapshot?: PaymentRequestTermsLike | null
): number | null {
  if (snapshot?.collection?.type === 'percent') {
    return validPercent(snapshot.collection.percent);
  }

  const percentages = new Set(
    (snapshot?.invoices || [])
      .map((invoice) => invoice.billing_terms_snapshot_json?.deposit)
      .filter((deposit) => deposit?.type === 'percent')
      .map((deposit) => validPercent(deposit?.value))
      .filter((percent): percent is number => percent !== null)
  );

  return percentages.size === 1 ? Array.from(percentages)[0] : null;
}

export function buildDepositRequestTitle(
  isQuoteDeposit: boolean,
  depositPercent: number | null
): string {
  if (isQuoteDeposit) {
    return 'Secure your charter reservation';
  }
  return depositPercent
    ? `${depositPercent}% deposit request`
    : 'Deposit request';
}

export function describeDepositTiming(terms?: BillingTermsLike | null): string {
  const deposit = terms?.deposit;
  if (!deposit || deposit.type === 'none' || Number(deposit.value || 0) <= 0) {
    return 'Deposit timing and balance billing follow the accepted terms.';
  }

  if (deposit.due === 'with_invoice') {
    return 'The deposit invoice and payment request are created when the operator issues the deposit. The balance is billed separately.';
  }
  if (deposit.due === 'before_trip') {
    const hours = Number(deposit.due_hours_before_service);
    const timing =
      Number.isFinite(hours) && hours > 0
        ? `${hours} hours before service`
        : 'before service';
    return `The deposit obligation is created on acceptance and is due ${timing}. The balance is billed separately.`;
  }
  if (deposit.due === 'on_acceptance') {
    return 'The deposit obligation is created on quote acceptance. The balance is billed separately.';
  }

  return 'Deposit timing and balance billing follow the accepted terms.';
}
